import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  useTheme,
  Button,
  Divider,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Flag as FlagIcon,
  CalendarToday,
  LocationOn,
  Link as LinkIcon,
  Public,
  Lock,
  Delete,
} from '@mui/icons-material';
import { eventService } from '@/services/events/eventService';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '@/components/common';

interface EventReport {
  _id: string;
  eventId: any;
  reportedBy: { _id: string; name?: string; email?: string };
  reason: string;
  createdAt: string;
  updatedAt: string;
}

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  return moment(iso).format('ddd, DD MMM YYYY • hh:mm A');
};

const EventReportsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [reports, setReports] = useState<EventReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchReports = async (pageNum: number, append = false) => {
    setIsLoading(true);
    try {
      const response: any = await eventService.getEventReports(pageNum, 10);
      if (response?.success) {
        const fetched: EventReport[] = response.data?.reports || [];
        setReports(prev => (append ? [...prev, ...fetched] : fetched));
        setHasMore(Boolean(response.data?.pagination?.hasNext));
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to fetch event reports');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(1);
  }, []);

  const handleRefresh = () => {
    setPage(1);
    fetchReports(1);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReports(nextPage, true);
  };

  const handleDeleteClick = (e: React.MouseEvent, eventId: string, eventName: string) => {
    e.stopPropagation();
    setEventToDelete({ id: eventId, name: eventName });
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setEventToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    try {
      setIsDeleting(true);
      await eventService.deleteEvent(eventToDelete.id);
      toast.success(`"${eventToDelete.name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setEventToDelete(null);
      setPage(1);
      fetchReports(1);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete event');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Reported Events
        </Typography>
        <IconButton onClick={handleRefresh} disabled={isLoading} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {reports.length === 0 && !isLoading ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary">
            No event reports to review at this time.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {reports.map((report) => {
            const event = report.eventId;
            return (
              <Box key={report._id} sx={{ mb: 4 }}>
                {/* Report meta */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 1,
                    bgcolor: theme.palette.mode === 'light' ? '#fff4f4' : '#2d1a1a',
                    border: `1px solid ${theme.palette.error.light}`,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <FlagIcon color="error" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="bold" color="error">
                      Reported by {report.reportedBy?.name || 'Unknown'} ({report.reportedBy?.email})
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Reason: <strong>{report.reason}</strong> • {moment(report.createdAt).fromNow()}
                    </Typography>
                  </Box>
                </Paper>

                {/* Event card */}
                {event ? (
                  <Paper
                    elevation={0}
                    onClick={() => navigate(`/events/${event._id}`)}
                    sx={{
                      p: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: theme.shadows[3] },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                        <Chip label={event.category} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        <Chip
                          label={event.eventMode === 'online' ? 'Online' : 'In Person'}
                          color={event.eventMode === 'online' ? 'info' : 'default'}
                          size="small"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        <Chip
                          label={event.ticketType === 'free' ? 'Free' : `₹${event.price}`}
                          color={event.ticketType === 'free' ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontSize: '0.7rem' }}
                        />
                        {event.isDeleted && <Chip label="Deleted" size="small" color="error" sx={{ fontSize: '0.7rem' }} />}
                        {!event.isActive && <Chip label="Inactive" size="small" sx={{ fontSize: '0.7rem' }} />}
                      </Box>
                      {!event.isDeleted && (
                        <Tooltip title="Delete Event">
                          <IconButton
                            size="small"
                            onClick={(e) => handleDeleteClick(e, event._id, event.eventName)}
                            sx={{ color: 'error.main' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                      {event.eventName}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(event.startDateTime)}
                        {event.endDateTime ? ` → ${formatDate(event.endDateTime)}` : ''}
                      </Typography>
                    </Box>

                    {event.eventMode === 'in_person' && event.venueName && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <LocationOn sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">{event.venueName}</Typography>
                      </Box>
                    )}
                    {event.eventMode === 'online' && event.eventLink && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <LinkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="primary" noWrap>{event.eventLink}</Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      {event.eventVisibility === 'public'
                        ? <Public sx={{ fontSize: 14, color: 'text.secondary' }} />
                        : <Lock sx={{ fontSize: 14, color: 'text.secondary' }} />}
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                        {event.eventVisibility}
                      </Typography>
                    </Box>

                    {event.organizer && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, pt: 1, borderTop: `1px dashed ${theme.palette.divider}` }}>
                        <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                          {(event.organizer.name || 'U').charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                            Organizer
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {event.organizer.name} • {event.organizer.email}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                ) : (
                  <Paper sx={{ p: 2, textAlign: 'center', border: `1px dashed ${theme.palette.divider}` }}>
                    <Typography variant="body2" color="text.secondary">
                      Reported event has been deleted or is unavailable.
                    </Typography>
                  </Paper>
                )}
                <Divider sx={{ mt: 2 }} />
              </Box>
            );
          })}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={isLoading}
                sx={{ borderRadius: '20px', px: 4 }}
              >
                {isLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : 'Load More Reports'}
              </Button>
            </Box>
          )}

          {!hasMore && reports.length > 0 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
              You've reviewed all event reports.
            </Typography>
          )}
        </Box>
      )}

      {isLoading && reports.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Event"
        message={`Are you sure you want to delete "${eventToDelete?.name}"? This action cannot be undone.`}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        severity="error"
      />
    </Box>
  );
};

export default EventReportsPage;
